import { useEffect, useMemo, useState, useCallback, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowRight, Rocket, Sparkles } from 'lucide-react';
import type { TrendData } from '@knitknot/shared/components/report';
import { UnifiedPageLayout } from '@knitknot/shared/components/ui/unified-page-layout';
import { ResearchBanner } from '@knitknot/shared/components/ui/research-banner';
import { ErrorState } from '@knitknot/shared/components/ui/error-state';
import { EmptyState } from '@knitknot/shared/components/ui/empty-state';
import { Skeleton } from '@knitknot/shared/components/ui/skeleton';
import { OverviewSkeleton } from '@/components/home/OverviewSkeleton';
import { Button } from '@knitknot/shared/components/ui/button';
import { SECTION_ACTION_CLASS, SectionHeader } from '@knitknot/shared/components/ui/section-header';
import { HomeScoreTrend } from '@/components/home/HomeScoreTrend';
import { AiVisitsModule } from '@/components/home/AiVisitsModule';
import {
  HeadToHeadPerformance,
  HeadToHeadTrend,
  VisibilityRanking,
  VisibilityTrend,
} from '@/components/home/CompetitivePosition';
import { CompetitiveHighlightProvider } from '@/components/home/competitiveHighlight';
import { NextActionsCard } from '@/components/home/NextActionsCard';
import { TopCitedPagesCard } from '@/components/home/TopCitedPagesCard';
import { WeekSummaryLine } from '@/components/home/WeekSummaryLine';
import { playbookPortfolioStory } from '@/components/playbook/playbookPortfolioStory';
import { useDataReadyTiming } from '@/contexts/PageTimingContext';
import { useWorkspaceStore } from '@/hooks/state';
import { companyService } from '@/services/company';
import { usePlaybookPortfolio, usePlaybooks } from '@/hooks/api/usePlaybooks';
import { usePeriodTrend } from '@/hooks/api/usePeriods';
import { buildPeriodTrendData } from '@/lib/periodTrend';
import { queryKeys } from '@/lib/queryKeys';
import { DEFAULT_LIST_STATUSES, type PlaybookListItem, type PlaybookStatus } from '@/types/playbooks';
import { useCoverage } from '@/hooks/api/useCoverage';
import { consoleRoutes } from '@/lib/routes';
import { formatDate } from '@knitknot/shared/utils';

const ACTIONABLE_STATUSES: ReadonlySet<PlaybookStatus> = new Set(['proposed', 'approved', 'in_progress']);

/** Stable `[]` for the pre-load render: a fresh literal would be a new identity
 *  every pass, and the two consumers below memoise off it (ship grouping, the
 *  shipped-page index). */
const NO_PLAYBOOKS: PlaybookListItem[] = [];

// ---------------------------------------------------------------------------
// BannerReveal — Rule 59 banner discipline: ResearchBanner/DismissibleBanner
// pop in above the fold post-load. Instead of shoving content down instantly,
// this animates height (0fr→1fr grid-template-rows) + opacity over 200ms on
// mount. `prefers-reduced-motion` is honored globally (base.css collapses all
// transition durations to ~0), so no extra guard is needed here.
// ---------------------------------------------------------------------------

function BannerReveal({ children }: { children: ReactNode }) {
  const [revealed, setRevealed] = useState(false);
  useEffect(() => {
    const raf = requestAnimationFrame(() => setRevealed(true));
    return () => cancelAnimationFrame(raf);
  }, []);
  return (
    <div
      className={
        'grid overflow-hidden transition-[grid-template-rows,opacity] duration-200 ease-out-quart ' +
        (revealed ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0')
      }
    >
      <div className="min-h-0">{children}</div>
    </div>
  );
}

/**
 * One line of benchmark news inside the "Latest benchmark" band: a lead-in, the
 * sentence, and a link to where it can be acted on.
 *
 * It exists because the page grew a SECOND full-width news row above this band —
 * a dismissible "AI surfaced N things to review" banner — saying the same kind
 * of thing in a different shape. Both are now rows here, which is why this is a
 * component rather than the markup written twice.
 *
 * NO trailing arrow (Max, 2026-09-03). The band already carries ONE arrow, in
 * the section header, and a row's own arrow made three of them stacked down the
 * right edge of a single card — the same affordance repeated until it stopped
 * reading as an affordance. The row is still a link: hover underlines the
 * sentence, exactly like the issue/playbook rows below it.
 */
function BenchmarkNewsRow({ to, lead, children }: { to: string; lead: string; children: ReactNode }) {
  return (
    <Link
      to={to}
      className="focus-ring group mt-3 block rounded-sm border-t border-border pt-3 text-sm"
    >
      <span className="min-w-0 text-ink-content group-hover:underline">
        <span className="font-medium text-foreground">{lead}</span>
        {children}
      </span>
    </Link>
  );
}

export default function OverviewPage() {
  const { workspaceId } = useWorkspaceStore();
  const queryClient = useQueryClient();
  const [isRetrying, setIsRetrying] = useState(false);

  const handleRetryResearch = useCallback(async () => {
    if (!workspaceId) return;
    setIsRetrying(true);
    try {
      await companyService.retryResearch(workspaceId);
      await queryClient.invalidateQueries({ queryKey: queryKeys.coverage.detail(workspaceId) });
    } catch {
      toast.error('Failed to restart research. Please try again.');
    } finally {
      setIsRetrying(false);
    }
  }, [workspaceId, queryClient]);
  const navigate = useNavigate();
  // The `?subject=` SWITCHER was deleted 2026-07-28 (Max: *"remove the subject
  // dropdown. We don't want this at all"*), and the page stopped READING the
  // param with it. Keeping the read would have left a scoping mechanism with no
  // visible control — a filter you cannot see you applied is the exact trap
  // rule 23 exists to prevent, and here it would have silently narrowed every
  // number on the dashboard. Home is workspace-wide, always. Per-product scope
  // still lives on the surfaces that own a subject (reports, /prompts).

  const {
    data: coverage,
    isPending: isPendingCoverage,
    isError: isCoverageError,
    refetch: refetchCoverage,
  } = useCoverage({
    refetchInterval: (query) => {
      const status = query.state.data?.company?.research_status;
      if (status && !['completed', 'failed'].includes(status)) return 5000;
      return false;
    },
  });

  const trendQ = usePeriodTrend();
  // ONE playbook fetch serves five consumers: the two competitive charts and
  // the AI Presence chart read shipped_at for their ship rules, Top cited pages
  // indexes shipped_url to mark a playbook page, WeekSummaryLine narrates the
  // week, and NextActionsCard re-filters to the actionable statuses.
  const playbooksQ = usePlaybooks(workspaceId ?? undefined, {
    status: DEFAULT_LIST_STATUSES,
  });
  const playbooks = playbooksQ.data ?? NO_PLAYBOOKS;
  const portfolioQ = usePlaybookPortfolio(workspaceId ?? undefined);
  const programStory = playbookPortfolioStory(portfolioQ.data?.earned);

  // The report uses this same canonical weekly series. Read it directly so
  // opening Overview never assembles unrelated report sections.
  const trendData: TrendData = useMemo(
    () => buildPeriodTrendData(trendQ.isPlaceholderData ? [] : trendQ.data ?? []),
    [trendQ.data, trendQ.isPlaceholderData],
  );
  const trendPending = !trendQ.isError && (trendQ.isPending || trendQ.isPlaceholderData);
  const trendUnavailable = trendQ.isError && (trendQ.isPlaceholderData || !trendQ.data);

  // -- Playbooks
  const actionablePlaybooks = useMemo(
    () => playbooks.filter((p) => ACTIONABLE_STATUSES.has(p.status)),
    [playbooks],
  );

  // `latestPoint` outlives the header it used to feed: the "Latest benchmark"
  // band still dates itself from the last measured point.
  const latestPoint = trendData.points.length > 0 ? trendData.points[trendData.points.length - 1] : null;

  const latestBenchmarkDate = latestPoint?.date ?? null;

  // Measures the benchmark summary/chart and analytics overlay, not lower widgets.
  const primaryPending = trendPending || playbooksQ.isPending || portfolioQ.isPending;
  const primaryFailed = playbooksQ.isError || portfolioQ.isError || trendQ.isError;
  useDataReadyTiming('overview-primary-ready', isCoverageError ? 'error'
    : coverage?.state !== 'active' || primaryPending ? null
    : primaryFailed ? 'partial' : trendData.points.length === 0 ? 'empty' : 'ready');

  if (isPendingCoverage) return <OverviewSkeleton />;

  if (isCoverageError || !coverage) {
    return (
      <UnifiedPageLayout
        title="Overview"
        description="Weekly performance, verified agent reach, and the work that needs attention next."
      >
        <div className="pt-10">
          <ErrorState
            title="Failed to load Overview"
            description="Check your connection and try again."
            onRetry={() => refetchCoverage()}
          />
        </div>
      </UnifiedPageLayout>
    );
  }

  // A workspace with no company is LOCKED by `OnboardingGate`, so it never gets
  // here — but leaving no branch would drop it into the "your company is ready"
  // state below, which would be a lie. The redirect that used to live here is
  // gone with it: one gate, one owner, no second redirect to disagree with.
  if (coverage.state === 'no_company') {
    return (
      <UnifiedPageLayout
        title="Overview"
        description="Weekly performance, verified agent reach, and the work that needs attention next."
      >
        <EmptyState
          icon={Rocket}
          situation="cold-start"
          title="This workspace isn't set up yet"
          description="Tell us your company's domain and we'll research how AI represents you."
          action={{ label: 'Start benchmark setup', href: '/onboarding', onClick: () => navigate('/onboarding') }}
        />
      </UnifiedPageLayout>
    );
  }

  // Legacy/unattended workspaces (gated=false) are NOT locked and still need the
  // progress banner while a re-research from Settings runs.
  const researchIncomplete = !!coverage.company?.research_status && coverage.company.research_status !== 'completed';
  if (coverage.state !== 'active' && researchIncomplete && coverage.company) {
    return (
      <UnifiedPageLayout
        title="Overview"
        description="Weekly performance, verified agent reach, and the work that needs attention next."
      >
        <div className="mx-auto mt-8 max-w-2xl">
          <ResearchBanner
            researchStatus={coverage.company.research_status!}
            researchJobId={coverage.company.research_job_id ?? null}
            companyName={coverage.company.name}
            onRetry={handleRetryResearch}
            isRetrying={isRetrying}
          />
        </div>
      </UnifiedPageLayout>
    );
  }

  if (coverage.state === 'no_runs') {
    return (
      <UnifiedPageLayout
        title="Overview"
        description="Weekly performance, verified agent reach, and the work that needs attention next."
      >
        <div className="mx-auto mt-16 max-w-xl pt-10 text-center">
          <Sparkles className="mx-auto mb-4 h-10 w-10 text-primary" />
          <h2 className="mb-2 text-xl font-semibold text-foreground">
            {coverage.company?.name ?? 'Your company'} is ready
          </h2>
          <p className="mb-5 text-sm text-muted-foreground">
            Setup is complete. Start your first run to see your AI Presence Score and where AI favors rivals.
          </p>
          {/* Show what onboarding actually built. "Ready" on its own is a claim,
              and it read identically whether the library held 93 prompts or
              none — which is exactly the state this page used to render over an
              empty library. Each count links to the page that owns it. */}
          {coverage.built && (
            <div className="mb-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-1.5 text-xs text-muted-foreground">
              {[
                {
                  label: 'rivals',
                  value: coverage.built.competitors,
                  to: consoleRoutes.knowledge.section('competitors'),
                },
                {
                  label: 'capabilities',
                  value: coverage.built.features,
                  to: consoleRoutes.knowledge.section('capabilities'),
                },
                {
                  label: 'demand themes',
                  value: coverage.built.topics,
                  to: consoleRoutes.demand.topics,
                },
                {
                  label: 'prompts',
                  value: coverage.built.prompts,
                  to: consoleRoutes.demand.questions,
                },
              ]
                .filter((s) => s.value > 0)
                .map((s) => (
                  <Link key={s.label} to={s.to} className="hover:text-foreground">
                    <span className="tabular-nums text-foreground">{s.value.toLocaleString()}</span> {s.label}
                  </Link>
                ))}
            </div>
          )}
          <Button size="sm" asChild className="h-7 gap-1.5 px-2.5 text-xs">
            <Link to={consoleRoutes.benchmarks.results}>Start first run</Link>
          </Button>
        </div>
      </UnifiedPageLayout>
    );
  }

  const trendReady = !trendPending && !trendUnavailable;
  const chartReady = trendReady && !playbooksQ.isPending;
  const newsReady = trendReady && !playbooksQ.isPending && !portfolioQ.isPending;

  return (
    <UnifiedPageLayout
      title="Overview"
      description="Weekly performance, verified agent reach, and the work that needs attention next."
    >
      {researchIncomplete && coverage.company && (
        <BannerReveal>
          <div className="pb-4">
            <ResearchBanner
              researchStatus={coverage.company.research_status!}
              researchJobId={coverage.company.research_job_id ?? null}
              companyName={coverage.company.name}
              onRetry={handleRetryResearch}
              isRetrying={isRetrying}
            />
          </div>
        </BannerReveal>
      )}


      <div className="space-y-4">
        {/* The weekly digest is NOT on this page (Max, 2026-09-04: *"get rid of
            the weekly digest completely from the overview, they can just get
            this in slack"*). It is a weekly READ delivered to Slack, and it was
            occupying the top of a page whose job is the current state — the
            trend chart and `WeekSummaryLine` below already narrate the live
            week. `ThisWeekCard` and its `layout="columns"` register are
            DELETED; the digest still renders on the period Changes tab, where
            the reader has asked for one specific closed week. */}

        <section aria-label="Latest benchmark" className="border-y border-border bg-card p-4 sm:p-5">
          <SectionHeader
            title="Latest benchmark"
            hint={trendReady ? (latestBenchmarkDate ? formatDate(latestBenchmarkDate) : 'Awaiting a completed benchmark') : undefined}
            /* Arrow only — the band's ONE drill (Max, 2026-09-03: *"let's just go
               with the arrow and not spell out the text 'Results'"*). The
               accessible name still says where it goes. */
            action={
              <Link
                to={consoleRoutes.benchmarks.results}
                className={SECTION_ACTION_CLASS}
                aria-label="View benchmark results"
              >
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            }
          />
          <div className="mt-2">
            {trendUnavailable ? (
              <ErrorState title="Could not load benchmark history" onRetry={() => trendQ.refetch()} />
            ) : !newsReady ? (
              <Skeleton className="h-5 w-3/4" aria-label="Loading benchmark summary" />
            ) : (
              <>
                {/* The measure caps the PROSE, not the band. It used to wrap the news
                    rows too, so their `border-t` stopped ~360px short of the band's
                    right edge and read as a broken rule. */}
                <div className="max-w-5xl">
                  <WeekSummaryLine
                    playbooks={playbooks}
                    fallback="Your latest benchmark is the measured baseline; the next run will establish movement."
                  />
                </div>
                {programStory && (
                  <BenchmarkNewsRow
                    to={`${consoleRoutes.publish.playbooks}?tab=done`}
                    lead="Published work: "
                  >
                    {programStory.headline} {programStory.detail}.
                  </BenchmarkNewsRow>
                )}
                {/* The AI Presence PLOT is not here any more (2026-09-18). A
                    single composite line did not warrant a full-width chart in
                    a band whose job is the week's news, and the score's real
                    story is what it does against the work the customer shipped
                    — so the plot moved down beside Playbooks, taking the ship
                    markers with it. What stays is what this band was for: the
                    dated sentences and the one `→` into the results. */}
              </>
            )}
          </div>
        </section>

        {/* The workspace "Benchmark trend" card is GONE (2026-09-18). It drew
            AI Presence, win rate, visibility and citation share on ONE axis, and
            three of those four sit on DIFFERENT denominators with nothing on
            screen to say so: visibility is organic-only, win rate is
            decided-head-to-head only, and citation share was taken over every
            answer including head-to-head — the conflation recorded at
            `core/metrics/periods.py:91`, which printed "Answers citing your
            pages 6" under "Citation share 22%" for one Mastra week.

            What replaces it is the two bands below: the same metrics, made
            PER-COMPETITOR and split by the population each is measured over, so
            every chart states its denominator and every series inside it shares
            one. The ship rules came with them — seeing a line move after a ship
            is better on a chart that also shows who you passed.

            The AI Presence score has no per-competitor analogue (it is a
            workspace composite), so it keeps its own slim readout in the band
            above rather than disappearing with the card. */}

        {/* Visibility: the chart on the left, the ranked list it summarises on
            the right. Both draw the same ten entities in the same order, so the
            last point of a line and the row beside it are one number. The chart
            takes the larger share because ten lines over a dozen weeks need the
            width; the list is a fixed set of short rows. */}
        {/* The provider is what makes each pair ONE object: hovering a row in
            the list isolates that competitor's line in the chart beside it, so
            neither chart needs a legend of its own (rule 65 — that band is
            gone). One provider PER BAND, so visibility and head-to-head never
            cross-isolate. */}
        <CompetitiveHighlightProvider>
          <div className="grid border-y border-border bg-card lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
            <div className="min-w-0 p-4 sm:p-5 lg:border-r lg:border-border">
              <VisibilityTrend />
            </div>
            <div className="min-w-0 border-t border-border p-4 sm:p-5 lg:border-t-0">
              <VisibilityRanking />
            </div>
          </div>
        </CompetitiveHighlightProvider>

        <CompetitiveHighlightProvider>
          <div className="grid border-y border-border bg-card lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
            <div className="min-w-0 p-4 sm:p-5 lg:border-r lg:border-border">
              <HeadToHeadTrend />
            </div>
            <div className="min-w-0 border-t border-border p-4 sm:p-5 lg:border-t-0">
              <HeadToHeadPerformance />
            </div>
          </div>
        </CompetitiveHighlightProvider>

        {/* Top cited pages takes the FULL width of the page. It shared a
            `lg:grid-cols-2` band with Playbooks, and the half it got was spent
            on the one column that cannot afford to lose any: the page URL is
            the row's identity (rule 69) and it was the only cell truncating.
            The two number columns are fixed-width, so every pixel of the extra
            width lands in the path. */}
        <div className="border-y border-border bg-card p-4 sm:p-5">
          <TopCitedPagesCard playbooks={playbooks} />
        </div>

        {/* "Your score against the work you shipped." The AI Presence plot and
            the playbook list are ONE object, which is why they share a band:
            the ▼ ship rules on the chart are the same plays listed beside it,
            and the hover names what shipped that week and how fast it was
            picked up. The chart sits on the LEFT like the two competitive
            charts above it — every plot on this page starts at the same edge.
            All four engines are drawn from the first paint (`engineSplit`), so
            "which assistant moved after we shipped" needs no click. */}
        <div className="grid border-y border-border bg-card lg:grid-cols-2">
          <div className="min-w-0 p-4 sm:p-5 lg:border-r lg:border-border">
            {/* The hint names the POPULATION, exactly as the two charts above
                it do ("Discovery answers, weekly" / "Comparison answers,
                weekly") — not the date, which the band at the top of the page
                already carries. */}
            <SectionHeader title="AI Presence" hint="All answers, weekly" />
            <div className="mt-2">
              {trendUnavailable ? (
                <ErrorState title="Could not load AI Presence" onRetry={() => trendQ.refetch()} />
              ) : chartReady ? (
                <HomeScoreTrend
                  trend={trendData}
                  metrics={['score']}
                  readout
                  engineSplit="score"
                  playbooks={playbooks}
                />
              ) : (
                <Skeleton className="h-52" aria-label="Loading AI Presence trend" />
              )}
            </div>
          </div>
          <div className="min-w-0 border-t border-border p-4 sm:p-5 lg:border-t-0">
            <NextActionsCard playbooks={actionablePlaybooks} loading={playbooksQ.isLoading} />
          </div>
        </div>

        <AiVisitsModule />
      </div>
    </UnifiedPageLayout>
  );
}
